import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import useEscapeClose from 'hooks/useEscapeClose';

const EMPTY = { audit_id: '', client_nom: '', signataire_nom: '', signataire_fonction: '', date_signature: '', notes: '' };

export default function DechargesPage() {
  const [decharges, setDecharges] = useState([]);
  const [audits, setAudits] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEscapeClose(showForm, () => setShowForm(false));

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [dRes, aRes] = await Promise.all([
        api.get('/audits/decharges'),
        api.get('/audits', { params: { statut: 'termine' } }).catch(() => ({ data: [] })),
      ]);
      setDecharges(dRes.data);
      setAudits(aRes.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/audits/decharges', form);
      setShowForm(false);
      setForm(EMPTY);
      load();
    } catch {}
    setSaving(false);
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div>
      <div className="content-card">
        <div className="gss-toolbar">
          <div className="content-card-title">Décharges signées</div>
          <button className="gss-btn gss-btn-primary" onClick={() => { setForm(EMPTY); setShowForm(true); }}>+ Nouvelle décharge</button>
        </div>

        {loading ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
        ) : decharges.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DDD" strokeWidth="1.5" style={{ marginBottom: 16 }}><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/></svg>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#666' }}>Aucune décharge</div>
          </div>
        ) : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr><th>Audit</th><th>Client</th><th>Signataire</th><th>Fonction</th><th>Date signature</th></tr>
              </thead>
              <tbody>
                {decharges.map(d => (
                  <tr key={d.id}>
                    <td><strong>{d.audit_numero || '-'}</strong></td>
                    <td>{d.client_nom || '-'}</td>
                    <td>{d.signataire_nom}</td>
                    <td>{d.signataire_fonction || '-'}</td>
                    <td>{d.date_signature?.split('T')[0] || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div>
                <h2>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  Nouvelle décharge
                </h2>
                <div className="gss-modal-subtitle">Document de décharge de responsabilité</div>
              </div>
              <button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '1.5rem' }}>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Audit associé</label>
                    <select className="gss-input" value={form.audit_id} onChange={e => {
                      const a = audits.find(x => x.id === e.target.value);
                      set('audit_id', e.target.value);
                      if (a) set('client_nom', a.client_nom || '');
                    }}>
                      <option value="">-- Sélectionner --</option>
                      {audits.map(a => <option key={a.id} value={a.id}>{a.numero} — {a.client_nom}</option>)}
                    </select>
                  </div>
                  <div className="gss-field"><label>Client</label><input className="gss-input" value={form.client_nom} onChange={e => set('client_nom', e.target.value)} /></div>
                  <div className="gss-field"><label>Signataire *</label><input className="gss-input" required value={form.signataire_nom} onChange={e => set('signataire_nom', e.target.value)} /></div>
                  <div className="gss-field"><label>Fonction</label><input className="gss-input" value={form.signataire_fonction} onChange={e => set('signataire_fonction', e.target.value)} /></div>
                  <div className="gss-field"><label>Date signature</label><input className="gss-input" type="date" value={form.date_signature} onChange={e => set('date_signature', e.target.value)} /></div>
                  <div className="gss-field gss-field-full"><label>Notes</label>
                    <textarea className="gss-input" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} style={{ height: 'auto', padding: '10px 12px' }} />
                  </div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>
                  {saving ? 'Enregistrement...' : 'Enregistrer la décharge'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
